import React from 'react';
import MonthlyChart from './charts/MonthlyChart';
import DailyChart from './charts/DailyChart';
import HalfHourlyChart from './charts/HalfHourlyChart';
import { ButtonDownload, ButtonPrimary } from '../../../../common/components';
import { DownloadButtonsWrapper } from '../../../mpans/MpanDetails.styles';
import * as colors from '../../../../common/styles/colors';
import { ChartsWrapper } from './energyusage.styles';

export type ViewMode = 'monthly' | 'daily' | 'halfhourly';

export interface ChartsProps {
  period: string;
  selectedMpans: string[];
  selectedCustomer: string;
  customerMonthlyData: any[];
  monthlySeries: Record<string, any[]> | null;
  dailySeries: Record<string, any[]> | null;
  hhSeries: Record<string, any[]> | null;
  canShowChart: boolean;
  canDownloadChartData: boolean;
  onMonthClick: (ym: string) => void;
  onDayClick: (d: string) => void;
  onBack: () => void;
  view: ViewMode;
  yearMonth?: string;
  date?: string;
  downloadReport: any;
}

const Charts: React.FC<ChartsProps> = ({
  period,
  selectedMpans,
  selectedCustomer,
  customerMonthlyData,
  monthlySeries,
  dailySeries,
  hhSeries,
  canShowChart,
  canDownloadChartData,
  onMonthClick,
  onDayClick,
  onBack,
  view,
  yearMonth,
  date,
  downloadReport,
}) => {
  let monthYear = yearMonth;
  if (yearMonth) {
    const [year, month] = yearMonth.split('-');
    const dateYearMonth = new Date(Number(year), Number(month) - 1);
    monthYear = dateYearMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }

  let dayMonthYear = date;
  if (date) {
    const [year, month, day] = date.split('-');
    const dateYearMonth = new Date(Number(year), Number(month) - 1, Number(day));
    dayMonthYear = dateYearMonth.toLocaleString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  }

  const handleOnBack = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    onBack();
  };

  const hasCustomerMonthlyData = customerMonthlyData && customerMonthlyData.length > 0 && selectedMpans.length === 0;
  const hasMonthlyData = monthlySeries && Object.values(monthlySeries).some(item => item.length > 0);
  const hasDailyData = dailySeries && Object.values(dailySeries).some(item => item.length > 0);
  const hasHalfHourlyData = hhSeries && Object.values(hhSeries).some(item => item.length > 0);

  const noGraphData =
    (view === 'monthly' && !hasCustomerMonthlyData && !hasMonthlyData) ||
    (view === 'halfhourly' && !hasHalfHourlyData) ||
    (view === 'daily' && !hasDailyData) ||
    false;

  const filterMessageTitle: Array<Messages.IFilterMessageType> = [
    { value: 'xlsx', display: 'Download Excel' },
    { value: 'xml', display: 'Download XML' },
  ];

  const disableHHorDailyData = (view === 'halfhourly' && !hasHalfHourlyData) || (view === 'daily' && !hasDailyData);

  return (
    <ChartsWrapper>
      <main className='main-panel container-fluid'>
        <DownloadButtonsWrapper className='row' key={'downloadbtn'}>
          <ButtonDownload
            className='ml-auto additional-buttons-spacing d-none d-md-inline'
            disabled={!canDownloadChartData || disableHHorDailyData}
            title={'Download graph data'}
            options={filterMessageTitle}
            onClick={downloadReport}
            outline={true}
            icon='upload'
            dontSaveSelectedIndex
          />
        </DownloadButtonsWrapper>

        {!canShowChart && <div className='text-center my-4'>Please select a customer</div>}

        {canShowChart &&
          view === 'monthly' &&
          (selectedCustomer && period && selectedMpans.length === 0 ? (
            <MonthlyChart
              mode='single'
              data={customerMonthlyData}
              onMonthClick={undefined}
              period={period as '6' | '12' | '24' | undefined}
              selectedMpans={[]}
            />
          ) : (
            monthlySeries &&
            (selectedMpans.length === 1 ? (
              <MonthlyChart
                mode='single'
                data={monthlySeries[selectedMpans[0]]}
                onMonthClick={onMonthClick}
                period={period as '6' | '12' | '24' | undefined}
                selectedMpans={selectedMpans}
              />
            ) : (
              <MonthlyChart
                mode='multi'
                series={selectedMpans.map(m => ({ label: m, entries: monthlySeries[m] }))}
                onMonthClick={onMonthClick}
                period={period as '6' | '12' | '24' | undefined}
                selectedMpans={selectedMpans}
              />
            ))
          ))}
        {canShowChart &&
          view === 'daily' &&
          (hasDailyData ? (
            selectedMpans.length === 1 ? (
              <DailyChart
                mode='single'
                data={dailySeries[selectedMpans[0]]}
                onDayClick={onDayClick}
                yearMonth={yearMonth}
                selectedMpans={selectedMpans}
              />
            ) : (
              <DailyChart
                mode='multi'
                series={selectedMpans.map(m => ({ label: m, entries: dailySeries[m] }))}
                onDayClick={onDayClick}
                yearMonth={yearMonth}
                selectedMpans={selectedMpans}
              />
            )
          ) : (
            <div className='text-center my-4'>No daily data for {monthYear}.</div>
          ))}
        {canShowChart &&
          view === 'halfhourly' &&
          (hasHalfHourlyData ? (
            selectedMpans.length === 1 ? (
              <HalfHourlyChart
                mode='single'
                data={hhSeries[selectedMpans[0]]}
                date={date}
                selectedMpans={selectedMpans}
              />
            ) : (
              <HalfHourlyChart
                mode='multi'
                series={selectedMpans.map(m => ({ label: m, entries: hhSeries[m] }))}
                date={date}
                selectedMpans={selectedMpans}
              />
            )
          ) : (
            <div className='text-center my-4'>No half-hourly data for {dayMonthYear}.</div>
          ))}

        {/* Chart legend */}
        {canShowChart && !noGraphData && (
          <div className='mt-3 d-flex flex-wrap justify-content-center text-center legend'>
            {selectedMpans.length <= 1 ? (
              <>
                <div className='d-flex align-items-center mb-2'>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 3,
                      background: colors.hydroBlue,
                      display: 'inline-block',
                      marginRight: 8,
                      border: `1px solid ${colors.hydroBlue}`,
                    }}
                  />
                  <span className='mr-2'>Actual</span>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 3,
                      background: colors.draxDarkBlue,
                      display: 'inline-block',
                      marginRight: 8,
                      border: `1px solid ${colors.draxDarkBlue}`,
                    }}
                  />
                  <span className='mr-2'>Estimated</span>
                  {view === 'monthly' && (
                    <>
                      <span
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 3,
                          background: colors.successLight,
                          display: 'inline-block',
                          marginRight: 8,
                          border: `1px solid ${colors.successLight}`,
                        }}
                      />
                      <span>Month to date</span>
                    </>
                  )}
                </div>
              </>
            ) : (
              selectedMpans.map((m, idx) => (
                <div key={m} className='d-flex align-items-center mr-4 mb-2'>
                  <svg width='24' height='10' aria-hidden className='mr-2'>
                    <line
                      x1='0'
                      y1='8'
                      x2='24'
                      y2='2'
                      stroke={require('../../utils/colors').colorForIndex(idx).stroke}
                      strokeWidth='3'
                      strokeLinecap='round'
                    />
                  </svg>
                  <span>{m}</span>
                </div>
              ))
            )}
          </div>
        )}

        <div className='row'>
          <div className='ml-auto'>
            <ButtonPrimary
              className='mt-0'
              title={view == 'halfhourly' ? 'Back to Daily' : 'Back to Monthly'}
              style={{ display: view === 'monthly' ? 'none' : 'inline-block' }}
              onClick={handleOnBack}
            />
          </div>
        </div>
      </main>
    </ChartsWrapper>
  );
};

export default Charts;
